import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from "react"
import { ResourceDetailsDialog } from "../../../../dialogs/resourceDetailsDialog/ResourceDetailsDialog"

const ResourceDetailsContext = createContext<{
    openResourceDetailsDialog: (id: string) => void
    closeResourceDetailsDialog: () => void
} | null>(null)

type Props = Readonly<PropsWithChildren<{}>>

export function ResourceDetailsProvider({ children }: Props) {
    const [resourceId, setResourceId] = useState<string | null>(null)

    const openResourceDetailsDialog = useCallback((id: string) => setResourceId(id), [])
    const closeResourceDetailsDialog = useCallback(() => setResourceId(null), [])

    const value = useMemo(
        () => ({
            openResourceDetailsDialog,
            closeResourceDetailsDialog,
        }),
        [openResourceDetailsDialog, closeResourceDetailsDialog],
    )

    return (
        <ResourceDetailsContext.Provider value={value}>
            {children}

            {resourceId && <ResourceDetailsDialog id={resourceId} handleClose={closeResourceDetailsDialog} />}
        </ResourceDetailsContext.Provider>
    )
}

export function useResourceDetails() {
    const context = useContext(ResourceDetailsContext)

    // If the hook is used outside of the Provider, context will be null
    if (!context) {
        throw new Error(
            "useResourceDetails must be used within a ResourceDetailsProvider. " +
                "Check that you have wrapped your component " +
                "in <ResourceDetailsProvider ...>.",
        )
    }

    return context
}
